// IMPORTAÇÃO de ESTRELAS/Potencial (classificação feita pelo vendedor) a partir da aba
// "Potencial_Estrelas" da planilha de roteirização. Casa por CODPARC e grava
// clients.estrelas / potencialVenda / potencialCompra APENAS nas células preenchidas — NUNCA
// zera classificação feita à mão (planilha vence só onde tem valor). O navegador só parseia e
// envia JSON; a escrita é server-side pela callable `importarPotencialEstrelas` (Admin SDK),
// porque só role 'admin' pode gravar esses campos e o casamento por CODPARC roda melhor no servidor.
import * as XLSX from 'xlsx';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';

const soDigitos = (s: unknown): string => String(s ?? '').replace(/\D/g, '');
// Normaliza cabeçalho: sem acento, minúsculo, sem espaço nas pontas ('Potencial (Estrelas)' -> 'potencial (estrelas)').
const normKey = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
// Número tolerante a pt-BR ("1.234,50") e simples ("1234.5"); vazio/NaN -> null.
function parseNum(v: unknown): number | null {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const t = s.replace(/[^\d,.\-]/g, '');
  const norm = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  const n = Number(norm);
  return Number.isFinite(n) ? n : null;
}

export interface LinhaEstrela {
  codparc: string;
  estrelas?: 1 | 2 | 3;
  potencialVenda?: number;
  potencialCompra?: string;
}
export interface ResumoImportEstrelas {
  total: number;       // linhas de dados na aba
  semCodparc: number;  // linhas sem CODPARC
  enviadas: number;    // linhas com CODPARC + ao menos um campo preenchido
  casados: number;     // CODPARC que casaram com cliente(s) na base
  semMatch: number;    // CODPARC sem cliente correspondente
  atualizados: number; // documentos (clients) efetivamente atualizados
}

// Acha a aba certa (por qualquer nome) e a LINHA de cabeçalho, tolerando o offset do título mesclado
// (a planilha do protótipo tem título na linha 1, linha 2 vazia, cabeçalho na linha 3). Procura em cada
// aba as primeiras ~10 linhas por uma que contenha as colunas exigidas por `temCabecalho`.
function acharCabecalho(
  wb: XLSX.WorkBook,
  temCabecalho: (cols: string[]) => boolean
): { aoa: unknown[][]; hdr: number } | null {
  for (const nome of wb.SheetNames) {
    // raw:true → números vêm crus (1000, não "1.000"): evita que o separador de milhar pt-BR
    // seja lido como decimal (bug: "1.000" viraria 1; "3.500" viraria 3.5). Cabeçalhos são texto.
    const aoa = XLSX.utils.sheet_to_json(wb.Sheets[nome], { header: 1, defval: '', raw: true }) as unknown[][];
    for (let i = 0; i < Math.min(10, aoa.length); i++) {
      const cols = (aoa[i] || []).map((v) => normKey(String(v)));
      if (temCabecalho(cols)) return { aoa, hdr: i };
    }
  }
  return null;
}

export async function importarPotencialEstrelas(file: File): Promise<ResumoImportEstrelas> {
  const wb = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: 'array' });

  // Cabeçalho válido = tem CODPARC e uma coluna de "estrelas".
  const achou = acharCabecalho(wb, (cols) =>
    cols.some((c) => c === 'codparc' || c.includes('codparc')) && cols.some((c) => c.includes('estrela'))
  );
  if (!achou) {
    throw new Error('Não encontrei a aba "Potencial_Estrelas" (precisa das colunas CODPARC e "Potencial (estrelas)").');
  }
  const { aoa, hdr } = achou;
  const cols = (aoa[hdr] as unknown[]).map((v) => normKey(String(v)));
  const idx = (pred: (c: string) => boolean): number => cols.findIndex(pred);
  const iCod = idx((c) => c === 'codparc' || c.includes('codparc'));
  const iEst = idx((c) => c.includes('estrela'));
  // Exige 'potencial' junto de venda/compra para NÃO casar colunas vizinhas ("Última Venda", "Vendas",
  // "Data da Compra") — num arquivo futuro elas gravariam um R$/texto errado silenciosamente. A aba do
  // protótipo usa exatamente "Potencial de Venda (R$)" e "Potencial de Compra".
  const iVen = idx((c) => c.includes('venda') && c.includes('potencial'));
  const iCom = idx((c) => c.includes('compra') && c.includes('potencial'));

  const linhasRaw = (aoa.slice(hdr + 1) as unknown[][]).filter((r) =>
    (r || []).some((v) => String(v ?? '').trim() !== '')
  );

  const linhas: LinhaEstrela[] = [];
  let semCodparc = 0;
  for (const r of linhasRaw) {
    const codparc = soDigitos(r[iCod]);
    if (!codparc) { semCodparc++; continue; }
    const en = iEst >= 0 ? parseNum(r[iEst]) : null;
    const estrelas = en === 1 || en === 2 || en === 3 ? (en as 1 | 2 | 3) : undefined;
    const vn = iVen >= 0 ? parseNum(r[iVen]) : null;
    const potencialVenda = vn != null && vn > 0 ? vn : undefined;
    const compraTxt = iCom >= 0 ? String(r[iCom] ?? '').trim() : '';
    const potencialCompra = compraTxt || undefined;
    if (estrelas === undefined && potencialVenda === undefined && potencialCompra === undefined) continue;
    const l: LinhaEstrela = { codparc };
    if (estrelas !== undefined) l.estrelas = estrelas;
    if (potencialVenda !== undefined) l.potencialVenda = potencialVenda;
    if (potencialCompra !== undefined) l.potencialCompra = potencialCompra;
    linhas.push(l);
  }

  const fn = httpsCallable<{ linhas: LinhaEstrela[] }, { casados: number; semMatch: number; atualizados: number }>(
    functions,
    'importarPotencialEstrelas',
    { timeout: 300000 }
  );
  const res = await fn({ linhas });
  const { casados, semMatch, atualizados } = res.data;
  return { total: linhasRaw.length, semCodparc, enviadas: linhas.length, casados, semMatch, atualizados };
}
